import axios from "axios"

// ============================================
// CONFIGURATION - BASE URL SETUP
// ============================================

const ENVIRONMENT = process.env.REACT_APP_API_ENV || "uat"

const BASE_URLS = {
  prod: "https://ticketbot-api-prod.ataibot.in",
  uat: "https://ticketbot-api-uat.ataibot.in",
}

// /public/api is the main backend prefix
const BASE_URL = `${BASE_URLS[ENVIRONMENT]}/public/api`
const CHATBOT_BASE_URL = `${BASE_URL}/chatbot`

const JSON_HEADERS = { "Content-Type": "application/json" }

// Helpers for cleaner calls
const chatbotGet = (path, config = {}) => axios.get(`${CHATBOT_BASE_URL}${path}`, config)
const chatbotPost = (path, data = {}, config = {}) =>
  axios.post(`${CHATBOT_BASE_URL}${path}`, data, { headers: JSON_HEADERS, ...config })

// ============================================
// SIMPLE CACHE FOR CLIENT DETAILS
// ============================================

const clientDetailsCache = new Map()

// ============================================
// SECTION 1: CLIENT & USER VERIFICATION
// ============================================

export const getClientDetails = async (clientId) => {
  if (!clientId) {
    throw new Error("clientId is required to fetch client details")
  }

  if (clientDetailsCache.has(clientId)) {
    return clientDetailsCache.get(clientId)
  }

  const requestPromise = chatbotGet(`/client/${clientId}`)
    .then((response) => response.data)
    .catch((error) => {
      clientDetailsCache.delete(clientId)
      throw error
    })

  clientDetailsCache.set(clientId, requestPromise)
  return requestPromise
}

export const getClientNameFromDetails = (details) => {
  return details?.data?.ClientName || ""
}

export const verifyMobileNumber = async (mobile, clientId) => {
  try {
    const response = await chatbotPost(`/get-user-id`, { mobile, clientId })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getClientContactNumber = async (clientId) => {
  try {
    const data = await getClientDetails(clientId)
    return data?.data?.ContactNumber || "8421924019"
  } catch {
    return "8421924019"
  }
}

// ============================================
// SECTION 2: COMPLAINTS & TICKETS (CHATBOT)
// ============================================

export const getUserDevices = async (userId) => {
  try {
    const response = await chatbotGet(`/search-subscriptions`, {
      params: { userID: userId },
      headers: JSON_HEADERS,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getComplaintsByProductId = async (productId) => {
  try {
    const response = await chatbotGet(`/complaints`, {
      params: { productID: productId },
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const getComplaintsByClientId = async (ClientId) => {
  try {
    const response = await chatbotGet(`/complaints`, {
      params: { ClientId },
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const createVisitorTicket = async ({ deviceID, complaintIDs, createdBy }) => {
  try {
    const response = await chatbotPost(`/tickets/visitor/create`, {
      deviceID,
      complaintIDs,
      createdBy,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const createAmcExpiredTicket = async ({ deviceID, complaintIDs, createdBy }) => {
  try {
    const response = await chatbotPost(`/tickets/amc-expired`, {
      deviceID,
      complaintIDs,
      createdBy,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const createAmcCallServiceTicket = async ({ deviceID, complaintIDs, createdBy }) => {
  try {
    const response = await chatbotPost(`/tickets/amc-call-service`, {
      deviceID,
      complaintIDs,
      createdBy,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const createNewVisitorAmcRequest = async ({ deviceID, complaintIDs, createdBy }) => {
  try {
    const response = await chatbotPost(`/create-new-visitor-amc-request`, {
      deviceID,
      complaintIDs,
      createdBy,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const createNewVisitorCallBasedService = async ({ deviceID, complaintIDs, createdBy }) => {
  try {
    const response = await chatbotPost(`/create-new-visitor-call-based-service`, {
      deviceID,
      complaintIDs,
      createdBy,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

// ============================================
// SECTION 3: RMS FLOW (Field Service)
// ============================================

export const getRMSAreasAndDevices = async (mobile, ClientId) => {
  try {
    const response = await chatbotPost(`/get-rms-devices`, {
      mobile,
      ClientId: Number.parseInt(ClientId),
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const scheduleRMSTicket = async ({ userId, areaCode, ClientId, productID, complaintID }) => {
  try {
    const response = await chatbotPost(`/schedule-rms-ticket`, {
      userId,
      areaCode,
      ClientId: Number.parseInt(ClientId),
      productID,
      complaintID,
    })
    return response.data
  } catch (error) {
    throw error
  }
}

// ============================================
// SECTION 4: CHATBOT CONVERSATION
// ============================================

export const initRecordingConversation = async (sessionId) => {
  try {
    const response = await chatbotPost(`/init_recording_conversation`, { user_id: sessionId })
    return response.data
  } catch (error) {
    throw error
  }
}

// ============================================
// SECTION 5: LEADBOT QUESTIONS & INQUIRY
// ============================================

export const fetchQuestions = async (clientId) => {
  if (!clientId) {
    throw new Error("No clientId provided for fetching questions")
  }

  try {
    const response = await chatbotGet(`/get-questions`, {
      params: { client_id: clientId },
    })
    return response.data
  } catch (error) {
    throw error
  }
}

export const submitInquiry = async (inquiryData) => {
  try {
    const response = await chatbotPost(`/manage-inquiry`, inquiryData)
    return response.data
  } catch (error) {
    return { message: "Failed to submit inquiry." }
  }
}

// ============================================
// SECTION 6: USER INTERACTION & FEEDBACK
// ============================================

export const submitUserQueryToBackend = async (sessionId, query, userDetails) => {
  if (!sessionId || !query || !userDetails?.userName || !userDetails?.userContact || !userDetails?.userEmail) {
    return { message: "Invalid query data. Please submit your details first." }
  }

  const requestBody = {
    user_id: sessionId,
    message: `${userDetails.userName}, ${userDetails.userContact}, ${userDetails.userEmail}`,
    user_query: query,
  }

  try {
    const response = await chatbotPost(`/submit_details`, requestBody)
    return response.data
  } catch (error) {
    return { message: error.response?.data?.message || "An error occurred while submitting the query." }
  }
}

export const submitCallbackPreference = async (sessionId, preference) => {
  try {
    const response = await chatbotPost(`/submit_callback_preference`, {
      user_id: sessionId,
      message: preference,
    })
    return response.data
  } catch (error) {
    return { message: "An error occurred. Please try again." }
  }
}

export const submitUserRating = async (sessionId, rating) => {
  try {
    const response = await chatbotPost(`/submit_satisfaction`, {
      user_id: sessionId,
      message: rating,
    })
    return response.data
  } catch (error) {
    return { message: "Failed to submit rating. Please try again." }
  }
}

// ============================================
// SECTION 7: SESSION TERMINATION
// ============================================

export const terminateChat = async (sessionId) => {
  try {
    const response = await chatbotPost(`/terminate`, { user_id: sessionId })
    return response.data
  } catch (error) {
    throw error
  }
}

export const handleTerminateResponse = async (sessionId, userMessage, clientId) => {
  if (!sessionId || typeof sessionId !== "string" || sessionId.trim() === "") {
    throw new Error("Invalid session ID provided for terminate_response API.")
  }

  if (!userMessage || (userMessage !== "Yes" && userMessage !== "No")) {
    throw new Error("Invalid response provided for terminate_response API.")
  }

  try {
    const payload = {
      user_id: sessionId,
      message: userMessage.trim(),
      client_id: clientId,
    }
    const response = await chatbotPost(`/terminate_response`, payload)
    return response.data
  } catch (error) {
    if (error.response && error.response.data) {
      throw error.response.data
    }
    throw new Error("Network error or unexpected issue occurred while calling terminate_response API.")
  }
}
